import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import shallow from 'zustand/shallow';

import { formatAsTimestamp } from 'lib/util';

import useVideoPlayerStore from 'components/video-player/store';

import { useAutoplayPreference } from 'lib/preferences/useAutoplayPreference';
import { useRouter } from 'next/router';
import { checkEnv } from '../gpt';
import { adConfig } from './adConfig';
import { useFetchSdk } from './fetchSdk';

const bigNumber = 2 ** 53 - 1;

function getToken() {
  return `${Math.floor(Math.random() * bigNumber).toString(36)}-${new Date()
    .getTime()
    .toString(36)}`;
}

// TODO: Enable ads for first-party sites
// https://tedconferences.atlassian.net/browse/WEB-2978

interface CuePoint {
  start: number;
  end: number;
  duration: number;
  preroll: boolean;
  postroll: boolean;
  played: boolean;
}

interface FetchAdProps {
  player: HTMLVideoElement | null;
  playbackSrc: string;
  adContainerRef: React.RefObject<HTMLDivElement>;
  setContentSrc: (src: string | null) => void;
  mainContentSrc: string | null;
  contentSourceId: string;
  adTag?: string;
  adParams?: Record<string, string>;
  maiTargeting?: Record<string, string>;
}

interface StreamEvent {
  getStreamData: () => {
    url?: string;
    streamId?: string;
    adProgressData?: {
      duration: number;
      currentTime: number;
    };
    cuepoints?: CuePoint[];
  };
  getAd: () => AdInfo;
}

interface AdInfo {
  getAdId: () => string;
  getTitle: () => string;
  getAdvertiserName: () => string;
  getDuration: () => number;
  getDescription: () => string;
  isSkippable: () => boolean;
  getCreativeId: () => string;
  getAdSystem: () => string;
  getDealId: () => string;
  getUniversalAdIdRegistry: () => string;
  getUniversalAdIds: () => string[];
  getWrapperAdIds: () => string[];
  getWrapperAdSystems: () => string[];
  getWrapperCreativeIds: () => string[];
  getCompanionAds: () => CompanionAd[];
}

interface CompanionAd {
  getWidth: () => number;
  getHeight: () => number;
  getContent: () => string;
}

interface AdData {
  adInfo: {
    id: string;
    title: string;
    advertiserName: string;
    duration: number;
    description: string;
    isSkippable: boolean;
    cues: CuePoint[];
    creativeId: string;
    lineItemId: string;
    adSystem: string;
    dealId: string;
    universalAdIdRegistry: string;
    universalAdIds: string;
    wrapperAdIds: string;
    wrapperAdSystems: string;
    wrapperAdCreativeIds: string;
  };
  companions: Record<string, string>[];
  contentSourceId: string;
  videoId: string;
  custParams: Record<string, string> | string;
  token: string;
  postrollDuration: number;
  prerollDuration: number;
  isAdBreak: boolean;
}

export function useFetchAd({
  player,
  playbackSrc,
  adContainerRef,
  setContentSrc,
  mainContentSrc,
  contentSourceId,
  adTag,
  adParams,
  maiTargeting
}: FetchAdProps) {
  const router = useRouter();
  const isEmbed = router.pathname.includes('embed');

  const [adContentSrc, setAdContentSrc] = useState<string | null>(null);
  const [adStreamId, setAdStreamId] = useState<string | undefined>(undefined);

  const [isAdBreak, setIsAdBreak] = useState<boolean>(false);
  const [counting, setCounting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [timeUntilSkip, setTimeUntilSkip] = useState<number>(5);
  const [isSkipCountdownActive, setIsSkipCountdownActive] =
    useState<boolean>(false);

  const [ad, setAd] = useState<AdData | null>(null);
  const [adTimeRemaining, setAdTimeRemaining] = useState(0);
  const [adError, setAdError] = useState<string | null>(null);

  const [streamManager, setStreamManager] = useState<unknown | null>(null);
  const [isPreroll, setIsPreroll] = useState<boolean>(false);
  const [prerollComplete, setPrerollComplete] = useState<boolean>(false);
  const [prerollStarted, setPrerollStarted] = useState<boolean>(false);
  const [postrollStarted, setPostrollStarted] = useState<boolean>(false);
  const [postrollComplete, setPostrollComplete] = useState<boolean>(false);

  const [minContentPlaybackMet, setMinContentPlaybackMet] =
    useState<boolean>(false);
  const MIN_CONTENT_PLAYBACK_SECONDS = 5; // Require at least 5 seconds of content playback

  // Add refs to track initialization state and prevent duplicate initializations
  const streamManagerInitializedRef = useRef<boolean>(false);
  const lastInitTimeRef = useRef<number>(0);

  // Add refs to track stream request state and prevent duplicate requests
  const streamRequestPendingRef = useRef<boolean>(false);
  const lastRequestTimeRef = useRef<number>(0);
  const lastRequestedSourceIdRef = useRef<string | null>(null);

  const [sdk, sdkError] = useFetchSdk({ debug: false });

  const cuesRef = useRef<CuePoint[]>([]);

  const [bufferTime, setBufferTime] = useState<number>(adConfig.bufferTime);

  const {
    expressPlaybackIntent,
    onAd,
    onAdLoaded,
    onContentState,
    onPrerollComplete,
    onPrerollStart,
    onPostrollComplete,
    onPostrollStart,
    onTalkComplete,
    contentState,
    streamTime,
    playbackMode,
    requestPlay,
    onPlaybackMode,
    streamDuration,
    prerollDuration,
    postrollDuration,
    audio
  } = useVideoPlayerStore(state => ({ ...state }), shallow);

  const [autoplayPreference] = useAutoplayPreference();

  const handleRequestPlayDebounced = useCallback(
    debounce((source: 'preroll' | 'content' | 'postroll') => {
      if (source !== 'content') {
        return;
      }
    }, 300),
    [contentState, autoplayPreference, playbackMode, requestPlay]
  );

  const handleSwitchToContentState = useCallback(() => {
    setIsAdBreak(false);
    setIsPreroll(false);
    handleRequestPlayDebounced('content');
    setContentSrc(playbackSrc);
    onContentState('content');
  }, [player, playbackSrc]);

  const handlePrerollStart = useCallback(() => {
    if (!prerollStarted && ad) {
      handleRequestPlayDebounced('preroll');
      setIsPreroll(true);
      setIsAdBreak(true);
      onPrerollStart(ad);
      onContentState('preroll');
      onPlaybackMode('playing');
      setPrerollStarted(true);
    }
  }, [prerollStarted, ad]);

  const handlePrerollCompletion = useCallback(() => {
    if (!prerollComplete) {
      onPrerollComplete();
      setIsAdBreak(false);
      expressPlaybackIntent(true);
      onContentState('content');
      onPlaybackMode('playing');
      setPrerollComplete(true);
    }
  }, [prerollComplete]);

  const handlePostrollStart = useCallback(() => {
    // Ensure we update state before modifying player time
    onContentState('postroll');
    setPostrollStarted(true);
    setIsAdBreak(true);

    // Finally trigger other state updates
    onTalkComplete();
    onPostrollStart(ad);
    onPlaybackMode('playing');
  }, [ad, player, streamTime, postrollDuration]);

  const handlePostrollCompletion = useCallback(() => {
    if (!postrollComplete) {
      onPostrollComplete();
      setIsAdBreak(false);
      onContentState('none');
      setPostrollComplete(true);
    }
  }, [onContentState, onPostrollComplete, postrollComplete]);

  const adEventHandlers = useMemo(
    () => ({
      loaded: (e: StreamEvent) => {
        const { url, streamId } = e.getStreamData();
        if (url) setContentSrc(url);
        if (url) setAdContentSrc(url);
        setAdStreamId(streamId);
        onAdLoaded();
      },
      started: (e: StreamEvent) => {
        const ad = e.getAd();
        const adIsSkippable = ad.isSkippable();

        const adInfo = {
          id: ad.getAdId(),
          title: ad.getTitle(),
          advertiserName: ad.getAdvertiserName(),
          duration: ad.getDuration(),
          description: ad.getDescription(),
          isSkippable: ad.isSkippable(),
          cues: cuesRef.current,

          // The following values are for reporting
          creativeId: ad.getCreativeId(),
          lineItemId: ad.getAdId(),
          adSystem: ad.getAdSystem(),
          dealId: ad.getDealId(),
          universalAdIdRegistry: ad.getUniversalAdIdRegistry(),
          universalAdIds: ad.getUniversalAdIds().join(','),
          wrapperAdIds: ad.getWrapperAdIds().join(','),
          wrapperAdSystems: ad.getWrapperAdSystems().join(','),
          wrapperAdCreativeIds: ad.getWrapperCreativeIds().join(',')
        };

        if (adIsSkippable && ad.getDuration() > 15 && ad.getDuration() <= 60) {
          setCounting(true);
          setIsSkipCountdownActive(true);
        }

        const companionAds = e.getAd().getCompanionAds();
        const companions = companionAds.map((ad: CompanionAd) => ({
          [`${ad.getWidth()}x${ad.getHeight()}`]: ad.getContent()
        }));

        // Mark any existing companion ad slots as protected
        if (companions.length > 0) {
          const companionAd = '[data-ad-companion="true"]';
          const protectedAd = '[data-ad-protected="true"]';
          const companionSlots = document.querySelectorAll(companionAd);

          // Check if slots already exist
          if (companionSlots.length > 0) {
            companionSlots.forEach(slot => {
              (slot as HTMLElement).dataset.adProtected = 'true';
            });
          }

          // See if any slots are added later
          const observer = new MutationObserver(mutations => {
            const newSlots = document.querySelectorAll(
              companionAd + ':not(' + protectedAd + ')'
            );
            if (newSlots.length > 0) {
              newSlots.forEach(slot => {
                (slot as HTMLElement).dataset.adProtected = 'true';
              });
            }
          });

          observer.observe(document.body, {
            childList: true,
            subtree: true
          });

          // Disconnect to prevent memory leaks
          setTimeout(() => observer.disconnect(), 3000);
        }

        const searchParams = new URLSearchParams(adParams?.cust_params || '');
        const adTagParams: Record<string, string> = Object.fromEntries(
          searchParams.entries()
        );

        if (adParams?.description_url) {
          adTagParams.description_url = adParams.description_url;
        }

        const prerollCue = cuesRef.current.find(cue => cue.preroll);
        const postrollCue = cuesRef.current.find(cue => cue.postroll);

        const adData: AdData = {
          adInfo,
          companions,
          contentSourceId:
            process.env.NEXT_PUBLIC_DAI_AD_CONTENT_SOURCE_ID || '',
          videoId: contentSourceId,
          custParams: adTagParams,
          token: getToken(),
          postrollDuration: postrollCue?.duration || 0,
          prerollDuration: prerollCue?.duration || 0,
          isAdBreak: true
        };

        setIsAdBreak(true);
        setAd(adData);
        onAd(adData);
      },
      error: (e: StreamEvent) => {
        const error = e.getStreamData();
        if (error) setAdError(String(error));
        setIsAdBreak(false);
      },
      ad_progress: (e: StreamEvent) => {
        const progressData = e.getStreamData().adProgressData;
        if (!progressData) return;

        const timeRemaining = Math.max(
          0,
          progressData.duration - progressData.currentTime - bufferTime
        );

        setAdTimeRemaining(timeRemaining);

        // Adjust buffer time when nearing the end of the ad
        if (
          timeRemaining <= adConfig.introSplashDuration &&
          bufferTime !== adConfig.introSplashDuration
        ) {
          setBufferTime(adConfig.introSplashDuration);
        }

        if (timeRemaining <= 0) {
          setIsSkipCountdownActive(false);
          setIsAdBreak(false);
          setCounting(false);
          setTimeUntilSkip(5);
          // Reset buffer time for the next ad
          setBufferTime(adConfig.bufferTime);
        }
      },
      cuepoints_changed: (e: StreamEvent) => {
        const streamData = e.getStreamData();
        const cuepoints = streamData.cuepoints || [];
        const videoDuration = player?.duration || 0;

        const prerollThreshold = 10; // Seconds from start
        const postrollThreshold = videoDuration - 60; // Seconds from end

        const newCuePoints = cuepoints.map(
          (cuePoint: { start: number; end: number }) => {
            const duration = cuePoint.end - cuePoint.start;
            const isPreroll = cuePoint.start <= prerollThreshold;
            const isPostroll = cuePoint.start >= postrollThreshold;

            return {
              ...cuePoint,
              duration,
              preroll: isPreroll,
              postroll: isPostroll,
              played: false
            };
          }
        );

        cuesRef.current = newCuePoints;

        const prerollCue = newCuePoints.find(
          (cue: { preroll: boolean }) => cue.preroll
        );
        const postrollCue = newCuePoints.find(
          (cue: { postroll: boolean }) => cue.postroll
        );

        if (prerollCue) {
          onAd({
            ...ad,
            prerollDuration: prerollCue.duration
          });
        }

        if (postrollCue) {
          onAd({
            ...ad,
            postrollDuration: postrollCue.duration
          });
        }

        if (!newCuePoints.some((cue: { preroll: boolean }) => cue.preroll)) {
          handleSwitchToContentState();
        }
      },
      paused: () => {
        setCounting(false);
      },
      resumed: () => {
        setCounting(true);
      },
      ad_break_ended: () => {
        setIsAdBreak(false);
        setCounting(false);
        setAdTimeRemaining(0);
        setTimeUntilSkip(5);
        setIsSkipCountdownActive(false);
      }
    }),
    [
      setContentSrc,
      onAdLoaded,
      adParams?.cust_params,
      contentSourceId,
      onAd,
      bufferTime
    ]
  );

  useEffect(() => {
    if (!player || !adContainerRef.current) {
      setAdError('Player or Ad container not found');
      return;
    }

    if (!sdk || sdkError) {
      setAdError(sdkError as string);
      setIsAdBreak(false);
      setLoading(false);
      return;
    }

    checkEnv();
    setAdError(null);

    if (!streamManager && sdk && adContainerRef.current) {
      const manager = new (sdk as any).ima.dai.api.StreamManager(
        player,
        document.getElementById('ad-info')
      );

      setStreamManager(manager);

      Object.entries(adEventHandlers).forEach(([event, handler]) => {
        manager.addEventListener(
          (sdk as any).ima.dai.api.StreamEvent.Type[event.toUpperCase()],
          handler,
          false
        );
      });

      setLoading(false);
    }
  }, [
    sdk,
    sdkError,
    player,
    adContainerRef,
    adTag,
    maiTargeting,
    adEventHandlers,
    streamManager
  ]);

  useEffect(() => {
    if (isEmbed) return;

    if (streamManager && !loading && adParams) {
      const streamRequest = new (sdk as any).ima.dai.api.VODStreamRequest();
      streamRequest.contentSourceId =
        process.env.NEXT_PUBLIC_DAI_AD_CONTENT_SOURCE_ID;
      streamRequest.videoId = contentSourceId;
      streamRequest.adTagParameters = adParams;
      (streamManager as any).requestStream(streamRequest);
    }
  }, [streamManager, adParams, loading, contentSourceId, isEmbed]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;
    if (isSkipCountdownActive && timeUntilSkip > 0 && counting) {
      intervalId = setInterval(() => {
        setTimeUntilSkip(prevTime => prevTime - 1);
      }, adConfig.timerIncrement);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [timeUntilSkip, isSkipCountdownActive, counting]);

  useEffect(() => {
    if (
      adError &&
      adError !== 'Player or Ad container not found' &&
      (contentState === 'none' ||
        contentState === 'preroll' ||
        contentState === 'content')
    ) {
      handleRequestPlayDebounced('content');
      handleSwitchToContentState();
      setContentSrc(playbackSrc);
    }
  }, [
    adError,
    playbackSrc,
    contentState,
    onContentState,
    streamTime,
    playbackMode
  ]);

  useEffect(() => {
    if (contentState === 'content' && isAdBreak) {
      setIsAdBreak(false);
    }
  }, [contentState, isAdBreak]);

  useEffect(() => {
    // Handles the case in which there is an ad url returned but no cuepoints
    // This is a fallback in case the ad server does not return cuepoints
    // In this case, we move on to the "content" state since there are actually no ads
    if (
      !adError &&
      !loading &&
      mainContentSrc &&
      mainContentSrc !== playbackSrc &&
      cuesRef.current.length === 0
    ) {
      handleSwitchToContentState();
    }
  }, [mainContentSrc, playbackSrc, cuesRef?.current]);

  useEffect(() => {
    // Track if we've met minimum content playback time to prevent accidental postroll triggers
    if (
      contentState === 'content' &&
      streamTime > (prerollDuration || 0) + MIN_CONTENT_PLAYBACK_SECONDS
    ) {
      if (!minContentPlaybackMet) {
        setMinContentPlaybackMet(true);
      }
    } else if (contentState !== 'content' && minContentPlaybackMet) {
      // Reset when not in content state
      setMinContentPlaybackMet(false);
    }
  }, [contentState, streamTime, prerollDuration, minContentPlaybackMet]);

  useEffect(() => {
    if (
      isEmbed ||
      !cuesRef.current.length ||
      !streamTime ||
      playbackMode === 'ended' ||
      playbackMode === 'unstarted'
    )
      return;

    const preroll = cuesRef.current.find(cue => cue.preroll);
    const postroll = cuesRef.current.find(cue => cue.postroll);

    if (!preroll && !postroll) return;

    // Handle preroll
    if (preroll && !prerollComplete) {
      if (!prerollStarted && streamTime < preroll.end) {
        if (!isPreroll) {
          setIsPreroll(true);
          handlePrerollStart();
          preroll.played = true;
        }
      }

      if (isPreroll && streamTime > preroll.end - bufferTime) {
        setIsPreroll(false);
        setPrerollComplete(true);
        handlePrerollCompletion();
      }
    }

    // For videos with only postroll, ensure preroll state is complete
    if (!preroll && !prerollComplete) {
      setPrerollComplete(true);
      onPrerollComplete();
      onContentState('content');
    }

    // Handle postroll
    if (postroll && !postrollComplete && player) {
      const currentDuration = player.duration;

      // Skip if duration is invalid
      if (!Number.isFinite(currentDuration) || currentDuration <= 0) {
        return;
      }

      // Calculate real content duration excluding pre/post roll
      const realContentDuration =
        currentDuration - (prerollDuration || 0) - (postrollDuration || 0);
      const hasPlayedMostContent =
        streamTime >= (prerollDuration || 0) + realContentDuration * 0.8;

      const shouldStartPostroll =
        !postrollStarted &&
        (!preroll || prerollComplete) && // Allow postroll if no preroll or preroll is complete
        minContentPlaybackMet && // Ensure minimum content playback time has been met
        streamTime >= postroll.start - bufferTime && // Standard postroll detection
        hasPlayedMostContent; // Ensure we've played at least 80% of content

      if (
        shouldStartPostroll &&
        Number.isFinite(currentDuration) &&
        currentDuration > 0
      ) {
        handlePostrollStart();
      }

      if (
        postrollStarted &&
        streamTime >= postroll.end &&
        Number.isFinite(currentDuration) &&
        currentDuration > 0
      ) {
        handlePostrollCompletion();
        postroll.played = true;
      }
    }
  }, [
    streamTime,
    isPreroll,
    prerollComplete,
    prerollStarted,
    postrollStarted,
    postrollComplete,
    playbackMode,
    isAdBreak,
    bufferTime,
    streamDuration,
    prerollDuration,
    postrollDuration,
    player,
    mainContentSrc,
    adContentSrc,
    contentState,
    loading,
    adError,
    playbackSrc,
    isEmbed,
    minContentPlaybackMet,
    audio
  ]);

  useEffect(() => {
    if (contentState === 'content') {
      // Reset ad states when returning to content
      setIsAdBreak(false);
      setAdTimeRemaining(0);
      setTimeUntilSkip(5);
      setIsSkipCountdownActive(false);

      // If we were in postroll, ensure proper cleanup
      if (postrollStarted && !postrollComplete) {
        handlePostrollCompletion();
      }
    }
  }, [contentState, postrollStarted, postrollComplete]);

  const isInitialMount = useRef(true);
  const previousContentSourceIdRef = useRef(contentSourceId);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;

      if (isEmbed) {
        setTimeout(handleSwitchToContentState, 0);
      }
    } else {
      // Reset ad-related state
      setIsAdBreak(false);
      setLoading(false);
      setAdError(null);
      setStreamManager(null);
      setIsSkipCountdownActive(false);
      setTimeUntilSkip(5);
      setAdTimeRemaining(0);
      setCounting(false);
      setIsPreroll(true);
      setPrerollComplete(false);
      setPrerollStarted(false);
      setPostrollStarted(false);
      setPostrollComplete(false);

      // Clear existing cue points
      cuesRef.current.splice(0, cuesRef.current.length);

      // Reset initialization tracking when content changes
      streamManagerInitializedRef.current = false;
      lastInitTimeRef.current = 0;
      streamRequestPendingRef.current = false;
      lastRequestTimeRef.current = 0;
      lastRequestedSourceIdRef.current = null;

      setContentSrc(null);
    }

    previousContentSourceIdRef.current = contentSourceId;
  }, [contentSourceId, isEmbed]);

  return {
    isAdBreak,
    adTimeRemaining: formatAsTimestamp(adTimeRemaining),
    adStreamId,
    adError,
    loading,
    isSkipCountdownActive
  };
}
